/**
 * Fleet Consultant dashboard.
 *
 * Fleet Consultants work their own book (leads/appointments/test drives) the
 * same way other floor consultants do, so the base "My Day" numbers are the
 * same. What's different is Deal Desk: Fleet writes full tenders and bulk
 * quotes across the dealership, so their scorecard adds quote-centric tiles
 * (open quotes, sent in window, accepted in window, average turnaround).
 *
 * Backend fills {@code snapshot.mine} (shared with the Sales Consultant
 * dashboard) and {@code snapshot.mine.fleet} (Fleet-only, filled when the
 * caller carries FLEET_MANAGER — see RoleSnapshotController).
 */

import KpiTile from '../../components/dashboard/KpiTile';
import MyDay from '../MyDay';
import { splitSub } from '../../components/dashboard/NewUsedSplit';
import { ColumnChart } from '../../components/dashboard/Pictorial';

export default function FleetConsultantDashboard({ snapshot, periodLabel }) {
  const m = snapshot?.mine;
  const f = m?.fleet;

  return (
    <div className="app-content" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* ── ROW 1 — personal day ── */}
      <div className="grid-4">
        <KpiTile index={0} label="MY TASKS" periodLabel="Today" to="/tasks"
          value={m?.tasksDueToday ?? '—'}
          color={(m?.tasksOverdue ?? 0) > 0 ? 'var(--danger)' : 'var(--accent-primary)'}
          tone={(m?.tasksOverdue ?? 0) > 0 ? 'danger' : undefined}
          sub={m ? `${m.tasksOverdue ?? 0} overdue` : undefined} />
        <KpiTile index={1} label="MY APPOINTMENTS" periodLabel="Today" to="/appointments"
          value={m?.appointmentsToday ?? '—'} />
        <KpiTile index={2} label="MY NEW LEADS" periodLabel="Today" to="/leads"
          value={m?.newLeadsToday ?? '—'}
          sub={m?.newLeadsSplit ? splitSub(m.newLeadsSplit) : undefined} />
        <KpiTile index={3} label="NO NEXT STEP" periodLabel="Now" to="/leads?filter=no-next-step"
          value={m?.leadsWithoutNextEvent ?? '—'}
          color={(m?.leadsWithoutNextEvent ?? 0) > 0 ? 'var(--warning)' : 'var(--success)'}
          tone="warning" sub="set one for each" />
      </div>

      {/* ── ROW 2 — fleet deal-desk ── */}
      <div className="grid-4">
        <KpiTile index={0} label="OPEN QUOTES" periodLabel="Now" to="/quotes"
          value={f?.openQuotes ?? '—'}
          sub="draft + sent, not yet closed" />
        <KpiTile index={1} label="QUOTES SENT" periodLabel={periodLabel}
          value={f?.quotesSentInWindow ?? '—'}
          sub="went out to a customer" />
        <KpiTile index={2} label="QUOTES ACCEPTED" periodLabel={periodLabel}
          value={f?.quotesAcceptedInWindow ?? '—'}
          color={(f?.quotesAcceptedInWindow ?? 0) > 0 ? 'var(--success)' : undefined} />
        <KpiTile index={3} label="AVG TURNAROUND" periodLabel={periodLabel}
          value={f?.avgQuoteTurnaroundHours == null ? '—' : `${f.avgQuoteTurnaroundHours}h`}
          sub="created → sent" />
      </div>

      {/* ── ROW 3 — fleet activity sketch ── */}
      {f && (
        <div className="card">
          <div className="card-label" style={{ marginBottom: 8 }}>MY DEAL DESK · {String(periodLabel).toUpperCase()}</div>
          {(f.openQuotes ?? 0) + (f.quotesSentInWindow ?? 0) + (f.quotesAcceptedInWindow ?? 0) === 0 ? (
            <div className="text-xs text-muted" style={{ lineHeight: 1.6 }}>
              No fleet quotes yet — write your first from the Deal Desk and
              it will land here with its turnaround time.
            </div>
          ) : (
            <ColumnChart height={140} bars={[
              { label: 'Open',     value: f.openQuotes ?? 0,            color: 'var(--accent-primary)' },
              { label: 'Sent',     value: f.quotesSentInWindow ?? 0,    color: 'var(--info)' },
              { label: 'Accepted', value: f.quotesAcceptedInWindow ?? 0, color: 'var(--success)' },
            ]} />
          )}
        </div>
      )}

      {/* ── My Day board — same component the Sales Consultant dashboard
             embeds. `embedded` suppresses MyDay's own TopBar and stat row;
             without it the page grows a second search bar mid-scroll. ── */}
      <MyDay embedded />
    </div>
  );
}
